import { ProtoItem, ContentType } from "@ca.vmedia.tv/domains";
import {Strings} from "@/locales/types/enums";
import { computed, ref, type Ref, type ComputedRef } from "vue";
import { useFavoriteOnDemandsStore } from "@/stores/FavoritesOnDemand";
import { useFavoriteProgramsStore } from "@/stores/FavoritesPrograms";
import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
import { useApplicationStore } from "@/stores/Application";
import { storeToRefs } from 'pinia';

interface ExposeInterface {
    getFavStatusChanging: ComputedRef<boolean>;
    addToFavorites: (items: Array<ProtoItem>) => Promise<void>;
    deleteFromFavorites: (items: Array<ProtoItem>) => Promise<void>;
    addToApiFavorites: (items: Array<ProtoItem>,  hasTooltipMessage: boolean) => Promise<void>;
    deleteFromApiFavorites: (items: Array<ProtoItem>, hasTooltipMessage: boolean) => Promise<void>;
    hasDeletedIdsFavoriteOnDemands: (id: string) => boolean;
    hasDeletedIdsFavoritePrograms: (id: string) => boolean;
    hasDeletedIdsFavoriteChannels: (id: string) => boolean; 
    hasDeletedIdsFavorite: (id: string) => boolean; 
    deleteSavedIdsFavorites: () => Promise<void>;
}

export const useFavorite = () => {
    const ApplicationStore = useApplicationStore();
    const { setTooltipeShowing ,setTooltipeMessage } = ApplicationStore;
    const {
        isMyTvBasicRoute
    } = storeToRefs(ApplicationStore);
    
    const favoriteChannelsStore = useFavoriteChannelsStore();
    const {
        getDeletedIdsChannels
    } = storeToRefs(favoriteChannelsStore);
    const { 
        saveDeletedIds: saveDeletedIdsChannels,
        clearDeletedIds: clearDeletedIdsChannels,
        clearAllDeletedIds: clearAllDeletedIdsChannels
    } = favoriteChannelsStore;


    const favoriteOnDemandsStore = useFavoriteOnDemandsStore();
    const {
        getDeletedIdsOnDemands
    } = storeToRefs(favoriteOnDemandsStore);
    const { 
        saveDeletedIds: saveDeletedIdsOnDemands,
        clearDeletedIds: clearDeletedIdsOnDemands,
        clearAllDeletedIds: clearAllDeletedIdsOnDemands
    } = favoriteOnDemandsStore;


    const favoriteProgramsStore = useFavoriteProgramsStore();
    const {
        getDeletedIdsPrograms
    } = storeToRefs(favoriteProgramsStore);
    const { 
        saveDeletedIds: saveDeletedIdsPrograms,
        clearDeletedIds: clearDeletedIdsPrograms,
        clearAllDeletedIds: clearAllDeletedIdsPrograms
    } = favoriteProgramsStore;
    

    const { $domains, $i18n } = useNuxtApp();
    const _isFavoriteStatusChanging: Ref<boolean> = ref(false);
    const ShowingTime = 5000;

    const getFavStatusChanging: ComputedRef<boolean> = computed(() => _isFavoriteStatusChanging.value);

    const hasDeletedIdsFavoriteOnDemands = (id: string): boolean => getDeletedIdsOnDemands.value.has(id);
    const hasDeletedIdsFavoritePrograms = (id: string): boolean => getDeletedIdsPrograms.value.has(id);
    const hasDeletedIdsFavoriteChannels = (id: string): boolean => getDeletedIdsChannels.value.has(id);
    const hasDeletedIdsFavorite = (id: string): boolean => hasDeletedIdsFavoriteOnDemands(id) || hasDeletedIdsFavoritePrograms(id) || hasDeletedIdsFavoriteChannels(id);

    const addToFavorites = async (items: Array<ProtoItem>): Promise<void> => {
        _isFavoriteStatusChanging.value = true;

        const id = `${items[0].id}:${items[0].contentType}`;

        if(hasDeletedIdsFavorite(id)) {
            switch (items[0].contentType) {
                case ContentType.DVR:
                    clearDeletedIdsPrograms(id);
                    break;
                case ContentType.LiveTV:
                    clearDeletedIdsChannels(id);
                    break;
                default:
                    clearDeletedIdsOnDemands(id);
                    break;
            }
        } else {
            await addToApiFavorites(items);
        }
        _isFavoriteStatusChanging.value = false;
    };

    const deleteFromFavorites = async (items: Array<ProtoItem>): Promise<void> => {
        _isFavoriteStatusChanging.value = true;

        if(isMyTvBasicRoute.value) {
            const id = `${items[0].id}:${items[0].contentType}`;

            switch (items[0].contentType) {
                case ContentType.DVR:
                    saveDeletedIdsPrograms(id);
                    break;
                case ContentType.LiveTV:
                    saveDeletedIdsChannels(id);
                    break;
                default:
                    saveDeletedIdsOnDemands(id);
                    break;
            }
        } else {
            await deleteFromApiFavorites(items, false);
        }
        _isFavoriteStatusChanging.value = false;
    };

    const _convertDeletedIdsToProtoItems = (deletedIds: Set<string>): Array<ProtoItem> => {
        const items: Array<ProtoItem> = [];

        deletedIds.forEach((item: string) => {
            const [id, contentType] = item.split(':');
            items.push(new ProtoItem(Number(id), Number(contentType) as ContentType));
        });

        return items;
    }

    const deleteSavedIdsFavorites = async (): Promise<void> => {
        const items: Array<ProtoItem[]> = [];
        
        if(getDeletedIdsOnDemands.value.size !== 0) {
            const itemsOndemand: Array<ProtoItem> = _convertDeletedIdsToProtoItems(getDeletedIdsOnDemands.value);
            items.push(itemsOndemand);
        }

        if(getDeletedIdsPrograms.value.size !== 0) {
            const itemsPrograms: Array<ProtoItem> = _convertDeletedIdsToProtoItems(getDeletedIdsPrograms.value);
            items.push(itemsPrograms);
        }

        if(getDeletedIdsChannels.value.size !== 0) {
            const itemsChannels: Array<ProtoItem> = _convertDeletedIdsToProtoItems(getDeletedIdsChannels.value);
            items.push(itemsChannels);
        }

        items.forEach(async (item: Array<ProtoItem>) => {
            await $domains.favoritesRepository.deleteFromFavourites(item);
        });
        clearAllDeletedIds();
    }

    const clearAllDeletedIds = (): void => {
        clearAllDeletedIdsOnDemands();
        clearAllDeletedIdsChannels();
        clearAllDeletedIdsPrograms();
    }

    const addToApiFavorites = async (items: Array<ProtoItem>, hasTooltipMessage: boolean = true): Promise<void> => {
        const response = await $domains.favoritesRepository.addToFavourites(items);

        if(!hasTooltipMessage) {
            return;
        }

        if (response > 0) {
            setTooltipeMessage($i18n.t(Strings.SETTINGS_FAVORITES_SAVE));
        } else {
            setTooltipeMessage($i18n.t(Strings.SETTINGS_FAVORITES_ERROR));
        }
        setTooltipeShowing({visible: true, showingTime: ShowingTime});
    };

    const deleteFromApiFavorites = async (items: Array<ProtoItem>, hasTooltipMessage: boolean = true): Promise<void> => {
        const response = await $domains.favoritesRepository.deleteFromFavourites(items);

        if(!hasTooltipMessage) {
            return;
        }

        if (response > 0) {
            setTooltipeMessage($i18n.t(Strings.SETTINGS_FAVORITES_SAVE));
        } else {
            setTooltipeMessage($i18n.t(Strings.SETTINGS_FAVORITES_ERROR));
        }
        setTooltipeShowing({visible: true, showingTime: ShowingTime});
    };

    return<ExposeInterface>{
        hasDeletedIdsFavoriteOnDemands,
        hasDeletedIdsFavoritePrograms,
        hasDeletedIdsFavoriteChannels,
        hasDeletedIdsFavorite,
        deleteFromFavorites,
        addToFavorites,
        addToApiFavorites,
        deleteFromApiFavorites,
        deleteSavedIdsFavorites,
        getFavStatusChanging
    }
};
